import { createContext, useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { BlogDataModel } from "../../models/Blog";
import { fetchAPI, paginateItems } from "../../Helpers/utils";
import { UmbracoNode } from "../../models/UmbracoNode";

//----------------------------------------
// REDUCER
const pageSize: number = 6;

export enum BlogActionEnum {
  SET_ITEMS,
  FILTER_BY_CATEGORY,
  GET_PAGED_ITEMS
}

export type BlogState = {
  filterCategory: string,
  blogPosts: UmbracoNode[],
  filteredBlogPosts: UmbracoNode[],
  currentPage: number,
  totalRecords: number
}

const initialState = {
  filterCategory: '',
  blogPosts: [],
  filteredBlogPosts: [],
  currentPage: 1,
  totalRecords: 0
}

export type BlogAction = |
{ action: BlogActionEnum.SET_ITEMS, payload: UmbracoNode[] }
| { action: BlogActionEnum.FILTER_BY_CATEGORY, payload: string }
| { action: BlogActionEnum.GET_PAGED_ITEMS, payload: number }

export const BlogStateReducer = (state: BlogState, event: BlogAction) => {
  switch (event.action) {
    case BlogActionEnum.SET_ITEMS:
      return {
        ...state,
        currentPage: 1,
        blogPosts: [...event.payload],
        filteredBlogPosts: [...paginateItems([...event.payload], 1, pageSize)],
        totalRecords: event.payload.length
      }

    case BlogActionEnum.FILTER_BY_CATEGORY:
      const filteredBlogPosts = filterItems(event.payload);

      return {
        ...state,
        currentPage: 1,
        filterCategory: event.payload,
        filteredBlogPosts: [...paginateItems(filteredBlogPosts, 1, pageSize)],
        totalRecords: filteredBlogPosts.length
      }

    case BlogActionEnum.GET_PAGED_ITEMS:
      return {
        ...state,
        currentPage: event.payload,
        filteredBlogPosts: [...paginateItems(filterItems(state.filterCategory), event.payload, pageSize)]
      }

    default:
      return initialState;
  }

  function filterItems(category: string) {
    let items = [...state.blogPosts];
    if (category.length > 0) {
      items = items.filter(item => {
        return (item.properties['category'] as Array<string>)?.some(x => x === category)
      });
    }

    return items;
  }
}

//----------------------------------------
// CONTEXT
export type BlogContextType = {
  categories: string[],
  blogPosts: UmbracoNode[],
  filters: {
    category: string
  },

  totalRecords: number,
  currentPage: number,
  pageSize: number,

  filterByCategory: (category: string) => void,
  loadItems: (page: number) => void
}

export const BlogContext = createContext<BlogContextType | null>(null);

//----------------------------------------
// PROVIDER
export type BlogProviderModel = {
  source: number,
  children: React.ReactNode
}

export default function BlogProvider({ children, source }: BlogProviderModel) {
  const [state, dispatch] = useReducer(BlogStateReducer, initialState);
  const [categories, setCategories] = useState<string[]>([]);

  // get blog data
  useEffect(() => {
    async function getData() {
      const data = await fetchAPI<BlogDataModel>(`/api/blog/${source}`);
      setCategories(data.categories);
      dispatch({ action: BlogActionEnum.SET_ITEMS, payload: data.items });
    }

    getData();
  }, [source]);

  const filterByCategory = useCallback((category: string) => {
    dispatch({ action: BlogActionEnum.FILTER_BY_CATEGORY, payload: category });
  }, [])

  const loadItems = useCallback((page: number) => {
    dispatch({ action: BlogActionEnum.GET_PAGED_ITEMS, payload: page });
  }, []);

  const blogContext = useMemo(() => {
    return {
      categories,
      blogPosts: state.filteredBlogPosts,
      filters: {
        category: state.filterCategory
      },
      totalRecords: state.totalRecords,
      currentPage: state.currentPage,
      pageSize: pageSize,

      filterByCategory,
      loadItems
    }
  }, [
    categories,
    state,

    filterByCategory,
    loadItems
  ]);

  return (
    <BlogContext.Provider value={blogContext}>
      {children}
    </BlogContext.Provider>
  )
}